import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setIsNewMessage } from "../../../appRedux/actions/Setting";
import {
   filterChatUsers,
   filterInactiveRooms,
   snapshotToArray,
} from "../WidgetsSettings/ChatSettings/ChatScripts";
import { getDepartmentUsers } from "../../../services/restAPI/RestApi";
import {
   fetchFirebase,
   fetchFirebaseBy,
   updateNotificationList,
} from "../WidgetsSettings/ChatSettings/firebaseCRUD";
import ChatDocked from "../WidgetsSettings/ChatSettings/ChatOutlooks/ChatDocked";
import ChatWindow from "../WidgetsSettings/ChatSettings/ChatOutlooks/ChatModal/ChatModal";
import "../WidgetsSettings/ChatSettings/ChatStyle.less";
import projectConfig from "../../../projectConfig.json";
import { useChat } from "../../../context/ChatContext/ChatContext";

const Chat = () => {
   const [messages, setMessages] = useState([]);
   const [sortedRooms, setSortedRooms] = useState([]);
   const [users, setUsers] = useState([]);
   const [phpUsers, setPhpUsers] = useState();
   const [allMessages, setAllMessages] = useState([]);
   const [allRooms, setAllRooms] = useState([]);

   const { isChatDocked } = useSelector(({ settings }) => settings);
   const { isChatMinimized, currentRoom } = useSelector(({ chatSettings }) => chatSettings);

   const { oldId } = useSelector(({ user }) => user);
   const currentDepartmentId = useSelector(({ user }) => user.currentDepartmentId);
   const { chatData, setChatData } = useChat();
   const dispatch = useDispatch();

   useEffect(() => {
      if (projectConfig?.hideChat === false && currentDepartmentId) {
         getDepartmentUsers(currentDepartmentId)
            .then((res) => {
               const mapped = (res?.data?.getUsersInDepartment ?? []).map((user) => {
                  const nameParts = (user.full_name || "").split(" ");
                  return {
                     oldId: parseInt(user.id, 10),
                     lastName: nameParts[0] || "",
                     firstName: nameParts.slice(1).join(" ") || "",
                     email: user.email,
                     login: user.login,
                  };
               });
               const filtered = filterChatUsers(mapped);
               setPhpUsers(filtered);
               setChatData((prevState) => ({
                  ...prevState,
                  phpUsers: filtered,
                  filteredPhpUsers: filtered,
               }));
            })
            .catch((error) => {
               console.error("Failed to fetch department users:", error);
               setPhpUsers([]);
               setChatData((prevState) => ({
                  ...prevState,
                  phpUsers: [],
                  filteredPhpUsers: [],
               }));
            });
      }
   }, [currentDepartmentId]);

   useMemo(() => {
      let myRooms = [];

      return allRooms?.filter((room) => {
         //finding all my rooms
         if (room?.chatUsers && room?.chatUsers?.includes(oldId) && room?.status !== "archived") {
            myRooms.push(room);
         }
         setSortedRooms(myRooms);
      });
   }, [allRooms]);

   useEffect(() => {
      if (projectConfig?.hideChat === false) {
         //setting public / private / direct (user to user) rooms
         setChatData((prevState) => {
            return {
               ...prevState,
               publicRooms: allRooms?.filter(
                  (singleRoom) =>
                     singleRoom?.type === "public" &&
                     singleRoom?.type &&
                     singleRoom.status !== "archived",
               ),
            };
         });
         setChatData((prevState) => {
            try {
               const privateRooms = allRooms?.filter(
                  (singleRoom) =>
                     singleRoom?.chatUsers &&
                     singleRoom?.type === "private" &&
                     singleRoom?.chatUsers?.includes(oldId) &&
                     singleRoom.status !== "archived",
               );

               return {
                  ...prevState,
                  privateRooms,
                  filteredPrivateRooms: privateRooms,
               };
            } catch (err) {
               console.log(err);
               return [];
            }
         });
         setChatData((prevState) => {
            return {
               ...prevState,
               directRooms: allRooms?.filter(
                  (singleRoom) => singleRoom?.type && singleRoom?.type === "direct",
               ),
            };
         });
      }
   }, [allRooms]);

   useEffect(() => {
      if (projectConfig?.hideChat === false) {
         const fetchAllMessages = () => {
            fetchFirebase("messages/").on("value", (resp) => {
               setAllMessages([]);
               setAllMessages(snapshotToArray(resp));
               setChatData((prevState) => {
                  return {
                     ...prevState,
                     messages: snapshotToArray(resp),
                  };
               });
            });
         };
         const fetchAllRooms = () => {
            fetchFirebase("rooms/").on("value", (resp) => {
               setAllRooms(filterInactiveRooms(snapshotToArray(resp)));
               setChatData((prevState) => {
                  return {
                     ...prevState,
                     allRooms: filterInactiveRooms(snapshotToArray(resp)),
                  };
               });
            });
            fetchFirebase("users/").on("value", (resp) => {
               setUsers(snapshotToArray(resp));
               setChatData((prevState) => {
                  return {
                     ...prevState,
                     users: snapshotToArray(resp),
                     filteredPhpUsers: [
                        ...(prevState.filteredPhpUsers || []),
                        ...snapshotToArray(resp)?.filter((user) => user.userType === "bot"),
                     ],
                  };
               });
            });
         };
         fetchAllRooms();
         fetchAllMessages();
      }
   }, []);

   const antModalContent = document.querySelector(".ant-modal-content");
   const antContent = document.querySelector(".chat-modal");

   useEffect(() => {
      if (projectConfig?.hideChat === false) {
         //Chat notificator
         let anyChatNotification = false;
         sortedRooms.forEach((room) => {
            // New messages were not seen
            if (
               room.id === currentRoom?.id &&
               Array.isArray(room?.seenBy) &&
               !room?.seenBy?.includes(oldId) &&
               isChatMinimized === false
            ) {
               // Update seenBy - now they were seen
               const newSeenBy = [...room.seenBy, oldId];
               const seenByList = [...new Set(newSeenBy)];
               updateNotificationList("rooms/", room.key, seenByList);
            } else {
               // Check if in any other room is some not-seen message to show red dot notification
               if (Array.isArray(room?.seenBy) && !room?.seenBy?.includes(oldId)) {
                  // Global notification about all group messages
                  if (room?.type !== "direct") {
                     anyChatNotification = true;
                  } else if (room?.type === "direct") {
                     // Global notification about direct messages but only from our organization
                     room.chatUsers.forEach((chatUserId) => {
                        if (chatUserId !== oldId) {
                           // If it's not us then it's a sender so we are checking if he is in our organization
                           chatData?.phpUsers?.forEach((phpUser) => {
                              // If we find him in organization list then we can show the notification
                              if (phpUser.oldId === chatUserId) {
                                 anyChatNotification = true;
                              }
                           });
                        }
                     });
                  }
               }
            }
         });
         setChatData((prevState) => {
            return {
               ...prevState,
            };
         });

         dispatch(setIsNewMessage(anyChatNotification));
      }
   }, [allMessages, sortedRooms, currentRoom?.id, isChatMinimized, chatData?.phpUsers]);

   if (isChatDocked) {
      if (antContent && antModalContent) {
         antContent.classList.add("docked-mod");
         antModalContent.classList.add("docked-mod");
      }
      return <ChatDocked messages={messages} />;
   } else {
      return (
         <ChatWindow
            users={users}
            phpUsers={phpUsers}
            sortedRooms={sortedRooms}
            messages={messages}
            allRooms={allRooms}
         />
      );
   }
};

export default Chat;
